package org.example.project.enginepersonal

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.coroutines.launch
import org.example.project.dashboard.LocalScreenRefreshVersion
import org.example.project.dashboard.PopupEngineCard
import org.example.project.enginetabular.TabularScreen
import org.example.project.enginetabular.TabularSplitScreen
import org.example.project.enginetabular.TabularToolbarButton
import org.example.project.screens_cus.UrlaubsApi
import org.example.project.screens_cus.Urlaubsanspruch
import org.example.project.screens_cus.ZeitkontoJournalEintrag
import org.example.project.service.GlobalManager
import org.example.project.settings.AppDropdown
import org.example.project.settings.AppDropdownAppearance
import org.example.project.settings.AppDropdownMenuMode
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.settings.UISettings
import org.example.project.utils.All
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.value
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Style
import org.jetbrains.compose.web.dom.Text

private val buchungsjournalOverviewHeaders = listOf(
    "Datum",
    "Personalnummer",
    "Mitarbeiter",
    "Zeitprofil",
    "Urlaubsanspruch",
    "Urlaub reserviert",
    "Urlaub genommen",
    "Urlaub verf\u00fcgbar",
    "Zeitkonto",
    "Zeitreserve Soll",
    "Zeitreserve Ist",
    "Überstunden reserviert",
    "Überstunden genommen",
    "Überstunden verfügbar",
    "Tagesarbeitszeit",
    "Arbeitszeit monatlich"
)

private val buchungsjournalDetailHeaders = listOf(
    "Buchungsdatum",
    "Buchungsuhrzeit",
    "Vorgangstyp",
    "Vorgangsart",
    "Menge",
    "Bearbeiteter Tag",
    "Mengenart",
    "Personalnummer",
    "Mitarbeiter",
    "Gebucht von"
)

@Composable
fun BuchungsjournalScreen(art: String) {
    val scope = rememberCoroutineScope()
    val refreshVersion = maxOf(
        LocalScreenRefreshVersion.current,
        GlobalManager.jsonDomainVersion("zeit"),
        GlobalManager.jsonDomainVersion("personal"),
        GlobalManager.jsonDomainVersion("user"),
        GlobalManager.jsonDomainVersion("urlaub")
    )
    val currentYear = kotlin.js.Date().getFullYear().toInt()
    var users by remember(art) { mutableStateOf<List<All>>(emptyList()) }
    var allowances by remember(art, currentYear) { mutableStateOf<List<Urlaubsanspruch>>(emptyList()) }
    var selectedUserId by remember(art) { mutableStateOf("") }
    var showReclassDialog by remember { mutableStateOf(false) }
    var reclassDirection by remember { mutableStateOf("stunden_zu_urlaub") }
    var reclassDays by remember { mutableStateOf("1") }
    var reclassMessage by remember { mutableStateOf("") }
    var savingReclass by remember { mutableStateOf(false) }
    var journalEntries by remember(selectedUserId) {
        mutableStateOf<List<ZeitkontoJournalEintrag>>(emptyList())
    }
    var overviewFilter by remember { mutableStateOf<Map<String, String>?>(null) }
    var detailFilter by remember(selectedUserId) { mutableStateOf<Map<String, String>?>(null) }

    suspend fun reloadOverview() {
        users = UrlaubsApi.searchBookingJournalOverview(currentYear, overviewFilter.orEmpty())
            .sortedWith(compareBy<All> { buchungsjournalUserName(it).lowercase() }.thenBy { it.id })
        allowances = users
        if (selectedUserId.isNotBlank() && users.none { it.id == selectedUserId }) {
            selectedUserId = ""
        }
    }

    suspend fun reloadDetails() {
        journalEntries = if (selectedUserId.isBlank()) {
            emptyList()
        } else {
            detailFilter?.let { UrlaubsApi.searchTimeAccountJournal(selectedUserId, it) }
                ?: UrlaubsApi.loadTimeAccountJournal(selectedUserId)
        }
    }

    LaunchedEffect(art, currentYear, refreshVersion) {
        reloadOverview()
    }

    LaunchedEffect(selectedUserId, refreshVersion) {
        reloadDetails()
    }

    val allowanceByUser = allowances.associateBy { it.userId }
    val selectedUser = users.firstOrNull { it.id == selectedUserId }
    val selectedAllowance = allowanceByUser[selectedUserId]
    val selectedUserName = buchungsjournalUserName(selectedUser)
    val today = buchungsjournalToday()

    Style(BuchungsjournalStyles)

    TabularSplitScreen(
        screen1 = {
            TabularScreen(
                screenKey = "Buchungsjournal_Uebersicht",
                rollenKey = "Buchungsjournal",
                title = "Buchungsjournal · Mitarbeiterkonten",
                headers = buchungsjournalOverviewHeaders,
                models = users,
                data = users.map { user ->
                    val allowance = allowanceByUser[user.id] ?: Urlaubsanspruch(userId = user.id, jahr = currentYear)
                    user.id to listOf(
                        today,
                        user.personalnummer,
                        buchungsjournalUserName(user).ifBlank { user.id },
                        allowance.zeitprofilName.ifBlank { "-" },
                        buchungsjournalDays(allowance.gesamtanspruch),
                        buchungsjournalDays(allowance.urlaubReserviert),
                        buchungsjournalDays(allowance.urlaubGenommen),
                        buchungsjournalDays(allowance.verfuegbar),
                        buchungsjournalHours(allowance.stundenkonto),
                        buchungsjournalHours(allowance.zeitreserve),
                        buchungsjournalHours(allowance.zeitreserveIst),
                        buchungsjournalHours(allowance.reservierteUeberstunden),
                        buchungsjournalHours(allowance.ueberstundenGenommen),
                        buchungsjournalHours(allowance.zeitkontoVerfuegbar),
                        buchungsjournalHours(allowance.arbeitszeitTaeglich),
                        buchungsjournalHours(allowance.arbeitszeitMonatlich)
                    )
                },
                editable = false,
                hardReadOnly = true,
                nonEditableCols = buchungsjournalOverviewHeaders.toSet(),
                includeMetadata = false,
                allowCreate = false,
                allowSave = false,
                allowDelete = false,
                allowImportButton = false,
                allowExportButton = true,
                allowSettingsButton = true,
                allowRefreshButton = true,
                allowSearchButton = true,
                extraNavButtons = listOf(
                    TabularToolbarButton(
                        label = "Umwidmung",
                        icon = "images/changerequest.svg",
                        enabled = selectedUserId.isNotBlank(),
                        onClick = {
                            reclassMessage = ""
                            reclassDays = "1"
                            reclassDirection = "stunden_zu_urlaub"
                            showReclassDialog = true
                        }
                    )
                ),
                showToolbarExtras = true,
                showFilterRow = true,
                showFillerRows = false,
                useUserSettings = true,
                dataColumnWidthCss = "minmax(145px, 1fr)",
                verticalScroll = true,
                onRowClick = { selectedUserId = it },
                onRefresh = {
                    scope.launch {
                        reloadOverview()
                        reloadDetails()
                    }
                },
                onSearchWithFilters = { filters ->
                    scope.launch {
                        overviewFilter = filters
                        users = UrlaubsApi.searchBookingJournalOverview(currentYear, filters)
                        if (selectedUserId.isNotBlank() && users.none { it.id == selectedUserId }) {
                            selectedUserId = ""
                        }
                    }
                },
                onCommit = { }
            )
        },
        screen2 = if (selectedUserId.isBlank()) {
            null
        } else {
            {
                TabularScreen(
                    screenKey = "Buchungsjournal_Details",
                    rollenKey = "Buchungsjournal",
                    title = "Buchungen · ${selectedUserName.ifBlank { selectedUserId }}",
                    headers = buchungsjournalDetailHeaders,
                    data = journalEntries.map { entry ->
                        entry.id to listOf(
                            buchungsjournalIsoDate(entry.buchungsdatum),
                            entry.buchungsuhrzeit,
                            entry.vorgangstyp,
                            entry.vorgangsart,
                            buchungsjournalNumber(
                                entry.stunden.takeIf { it != 0.0 }
                                    ?: entry.stundenDelta.takeIf { it != 0.0 }
                                    ?: entry.arbeitsstunden
                            ),
                            buchungsjournalIsoDate(entry.datum),
                            entry.mengenart,
                            entry.personalnummer.ifBlank { selectedUser?.personalnummer.orEmpty() },
                            entry.userName.ifBlank { selectedUserName.ifBlank { entry.userId } },
                            entry.gebuchtVon.ifBlank { entry.erstelltVon.ifBlank { "System" } }
                        )
                    },
                    editable = false,
                    hardReadOnly = true,
                    nonEditableCols = buchungsjournalDetailHeaders.toSet(),
                    includeMetadata = false,
                    allowCreate = false,
                    allowSave = false,
                    allowDelete = false,
                    allowImportButton = false,
                    allowExportButton = true,
                    allowSettingsButton = true,
                    allowRefreshButton = true,
                    allowSearchButton = true,
                    showToolbarExtras = true,
                    showFilterRow = true,
                    onSearchWithFilters = { filters ->
                        scope.launch {
                            detailFilter = filters
                            journalEntries = UrlaubsApi.searchTimeAccountJournal(selectedUserId, filters)
                        }
                    },
                    showFillerRows = false,
                    useUserSettings = true,
                    dataColumnWidthCss = "minmax(160px, 1fr)",
                    verticalScroll = true,
                    onRefresh = { scope.launch { reloadDetails() } },
                    onCommit = { }
                )
            }
        },
        initialRatio = 0.52f,
        minRatio = 0.25f,
        maxRatio = 0.8f
    )

    if (showReclassDialog) {
        BuchungsjournalUmwidmungDialog(
            userName = selectedUserName.ifBlank { selectedUserId },
            allowance = selectedAllowance,
            direction = reclassDirection,
            days = reclassDays,
            message = reclassMessage,
            saving = savingReclass,
            onDirectionChange = { reclassDirection = it },
            onDaysChange = { reclassDays = it },
            onClose = { if (!savingReclass) showReclassDialog = false },
            onSave = {
                if (selectedUserId.isBlank() || savingReclass) return@BuchungsjournalUmwidmungDialog
                val days = reclassDays.replace(",", ".").toDoubleOrNull() ?: 0.0
                if (days <= 0.0) {
                    reclassMessage = "Tage eintragen."
                    return@BuchungsjournalUmwidmungDialog
                }
                savingReclass = true
                reclassMessage = ""
                scope.launch {
                    val saved = UrlaubsApi.saveReclassification(
                        selectedUserId,
                        currentYear,
                        reclassDirection,
                        days,
                        0.0
                    )
                    if (saved) {
                        reloadOverview()
                        reloadDetails()
                        showReclassDialog = false
                    } else {
                        reclassMessage = "Umwidmung konnte nicht gespeichert werden."
                    }
                    savingReclass = false
                }
            }
        )
    }
}

@Composable
private fun BuchungsjournalUmwidmungDialog(
    userName: String,
    allowance: Urlaubsanspruch?,
    direction: String,
    days: String,
    message: String,
    saving: Boolean,
    onDirectionChange: (String) -> Unit,
    onDaysChange: (String) -> Unit,
    onClose: () -> Unit,
    onSave: () -> Unit
) {
    PopupEngineCard(
        windowKey = "buchungsjournal-umwidmung-popup",
        widthPx = 520,
        heightPx = 390,
        minWidthPx = 420,
        minHeightPx = 320,
        initialX = 260.0,
        initialY = 130.0,
        resizable = true,
        viewportAnchored = true,
        onClose = onClose
    ) {
        Div({ classes(BuchungsjournalStyles.dialogBox) }) {
            Div({ classes(BuchungsjournalStyles.dialogHeader) }) {
                Span({ classes(BuchungsjournalStyles.dialogSubline) }) { Text(userName) }
                Div({ classes(BuchungsjournalStyles.dialogTitle) }) { Text("Umwidmung") }
                Span { Text("") }
            }
            Div({ classes(BuchungsjournalStyles.metricRow) }) {
                BuchungsjournalDialogMetric("Überstunden verfügbar", buchungsjournalHours(allowance?.zeitkontoVerfuegbar ?: 0.0))
                BuchungsjournalDialogMetric("Urlaub verfügbar", buchungsjournalDays(allowance?.verfuegbar ?: 0.0))
                BuchungsjournalDialogMetric("Tagesarbeitszeit", buchungsjournalHours(allowance?.arbeitszeitTaeglich ?: 0.0))
            }
            Div({ classes(BuchungsjournalStyles.field) }) {
                Span({ classes(BuchungsjournalStyles.label) }) { Text("Richtung") }
                AppDropdown(
                    options = listOf(
                        All(key = "stunden_zu_urlaub", label = "Urlaub generieren"),
                        All(key = "urlaub_zu_stunden", label = "Überstunden generieren")
                    ),
                    selectedValue = direction,
                    onChange = onDirectionChange,
                    enabled = !saving,
                    appearance = AppDropdownAppearance.Surface,
                    menuMode = AppDropdownMenuMode.Overlay
                )
            }
            Div({ classes(BuchungsjournalStyles.field) }) {
                Span({ classes(BuchungsjournalStyles.label) }) { Text("Wie viele Tage wollen Sie umwidmen?") }
                Input(InputType.Text, attrs = {
                    classes(BuchungsjournalStyles.input)
                    value(days)
                    if (saving) disabled()
                    onInput { onDaysChange(it.value) }
                })
            }
            if (message.isNotBlank()) {
                Div({ classes(BuchungsjournalStyles.message) }) { Text(message) }
            }
            Div({ classes(BuchungsjournalStyles.actions) }) {
                LumaticaButton(
                    label = "Abbrechen",
                    onClick = onClose,
                    enabled = !saving,
                    variant = LumaticaButtonVariant.State,
                    heightPx = 34
                )
                LumaticaButton(
                    label = if (saving) "Speichert..." else "Buchen",
                    onClick = onSave,
                    enabled = !saving,
                    variant = LumaticaButtonVariant.Action,
                    heightPx = 34
                )
            }
        }
    }
}

@Composable
private fun BuchungsjournalDialogMetric(label: String, value: String) {
    Div({ classes(BuchungsjournalStyles.metric) }) {
        Span({ classes(BuchungsjournalStyles.metricLabel) }) { Text(label) }
        Span({ classes(BuchungsjournalStyles.metricValue) }) { Text(value) }
    }
}

private fun buchungsjournalUserName(user: All?): String =
    user?.name?.ifBlank {
        listOf(user.vorname, user.nachname).filter { it.isNotBlank() }.joinToString(" ")
    }.orEmpty()

private fun buchungsjournalToday(): String {
    val today = kotlin.js.Date()
    return listOf(
        today.getDate().toInt().toString().padStart(2, '0'),
        (today.getMonth().toInt() + 1).toString().padStart(2, '0'),
        today.getFullYear().toInt().toString()
    ).joinToString(".")
}

private fun buchungsjournalIsoDate(iso: String): String {
    val parts = iso.split("-")
    if (parts.size != 3) return ""
    val year = parts[0].toIntOrNull() ?: return ""
    val month = parts[1].toIntOrNull() ?: return ""
    val day = parts[2].toIntOrNull() ?: return ""
    if (year <= 0 || month !in 1..12 || day !in 1..31) return ""
    return "${day.toString().padStart(2, '0')}.${month.toString().padStart(2, '0')}.$year"
}

private fun buchungsjournalDays(value: Double): String =
    "${buchungsjournalNumber(value)} Tage"

private fun buchungsjournalHours(value: Double): String =
    "${buchungsjournalNumber(value)} Std."

private fun buchungsjournalNumber(value: Double): String =
    if (value % 1.0 == 0.0) value.toInt().toString() else value.toString().replace(".", ",")

private object BuchungsjournalStyles : StyleSheet() {
    val dialogBox by style {
        width(100.percent)
        height(100.percent)
        padding(24.px, 22.px, 18.px, 22.px)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(14.px)
        boxSizing("border-box")
        overflow("hidden")
    }

    val dialogHeader by style {
        display(DisplayStyle.Grid)
        gridTemplateColumns("1fr auto 1fr")
        alignItems(AlignItems.Center)
        gap(12.px)
    }

    val dialogTitle by style {
        fontSize(18.px)
        fontWeight(700)
        color(Color.white)
        textAlign("center")
    }

    val dialogSubline by style {
        fontSize(18.px)
        fontWeight(700)
        color(Color.white)
        property("min-width", "0")
        property("overflow", "hidden")
        property("text-overflow", "ellipsis")
        property("white-space", "nowrap")
    }

    val closeButton by style {
        justifySelf("end")
        width(32.px)
        height(32.px)
        borderRadius(6.px)
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.16))
        backgroundColor(rgba(255, 255, 255, 0.04))
        color(UISettings.STATE_BUTTON_TEXT)
        cursor("pointer")
        hover {
            property("border-color", "rgba(255,255,255,0.30)")
            backgroundColor(rgba(255, 255, 255, 0.07))
        }
    }

    val metricRow by style {
        display(DisplayStyle.Grid)
        gridTemplateColumns("repeat(3, minmax(0, 1fr))")
        gap(8.px)
    }

    val metric by style {
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.14))
        borderRadius(8.px)
        padding(10.px)
        backgroundColor(rgba(255, 255, 255, 0.045))
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(4.px)
    }

    val metricLabel by style {
        fontSize(11.px)
        color(rgba(215, 221, 232, 0.66))
    }

    val metricValue by style {
        fontSize(15.px)
        fontWeight(700)
        color(Color.white)
    }

    val fieldGrid by style {
        display(DisplayStyle.Grid)
        gridTemplateColumns("repeat(2, minmax(0, 1fr))")
        gap(10.px)
    }

    val field by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(5.px)
    }

    val label by style {
        fontSize(12.px)
        fontWeight(600)
        color(UISettings.STATE_BUTTON_TEXT)
    }

    val input by style {
        height(34.px)
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.16))
        borderRadius(6.px)
        paddingLeft(10.px)
        paddingRight(10.px)
        fontSize(13.px)
        color(Color.white)
        backgroundColor(rgba(255, 255, 255, 0.055))
        property("outline", "none")
        focus {
            property("border-color", "${UISettings.ACCENT_HEX}")
            property("box-shadow", "0 0 0 2px ${UISettings.accentRgba(0.16)}")
        }
    }

    val message by style {
        fontSize(13.px)
        color(UISettings.ACCENT)
    }

    val actions by style {
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.FlexEnd)
        gap(8.px)
    }
}
